import { ChangeEvent, FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { InputField } from "components/inputs/InputField"
import { BLACKLIST_PLACEHOLDER, BLACKLIST_TYPE_OPTIONS } from "components/risk/constants"

import { riskService } from "services/risk/risk.service"
import { BlacklistEntry, BlacklistType } from "services/risk/types"

interface Props {
  entry: BlacklistEntry | null
  open: boolean
  onClose: () => void
  onSave: () => void
}

export const BlacklistEntryModal: FC<Props> = ({ entry, open, onClose, onSave }) => {
  const isEdit = !!entry
  const [form, setForm] = useState<{ type: BlacklistType; value: string; reason: string }>({
    type: "EMAIL",
    value: "",
    reason: ""
  })
  const [saving, setSaving] = useState(false)

  const updateField = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [field]: value }))

  useEffect(() => {
    if (open) {
      setForm({
        type: entry?.type || "EMAIL",
        value: entry?.value || "",
        reason: entry?.reason || ""
      })
    }
  }, [open, entry])

  const handleSave = useCallback(async () => {
    if (!form.value.trim()) {
      toast.error("Value is required")
      return
    }
    setSaving(true)
    try {
      if (isEdit && entry) {
        await riskService.updateBlacklistEntry(entry.id, {
          value: form.value.trim(),
          reason: form.reason.trim() || null
        } as Partial<BlacklistEntry>)
        toast.success("Blacklist entry updated")
      } else {
        await riskService.createBlacklistEntry({
          type: form.type,
          value: form.value.trim(),
          reason: form.reason.trim() || undefined
        })
        toast.success("Blacklist entry created")
      }
      onClose()
      onSave()
    } finally {
      setSaving(false)
    }
  }, [isEdit, entry, form, onClose, onSave])

  return (
    <dialog className={clsx("modal", open && "modal-open")} onClick={onClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-4">{isEdit ? "Edit Blacklist Entry" : "Add Blacklist Entry"}</h3>

        <div className="space-y-3">
          <DropdownMenu
            title="Type"
            options={BLACKLIST_TYPE_OPTIONS}
            value={form.type}
            disabled={isEdit}
            hideEmptyOption
            onChange={(e: ChangeEvent<HTMLSelectElement>) => updateField("type", e.target.value as BlacklistType)}
          />

          <InputField
            title="Value"
            placeholder={BLACKLIST_PLACEHOLDER[form.type] || "Enter value..."}
            value={form.value}
            onChange={(e: ChangeEvent<HTMLInputElement>) => updateField("value", e.target.value)}
            altLabelTwo={form.type === "NAME" ? "Names are matched case-insensitively" : undefined}
          />

          <div className="form-control">
            <label className="label">
              <span className="label-text">Reason (optional)</span>
            </label>
            <textarea
              className="textarea textarea-bordered w-full"
              placeholder="Why this entry is blacklisted..."
              value={form.reason}
              onChange={(e) => updateField("reason", e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <div className="modal-action">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.value.trim()}>
            {saving && "Saving..."}
            {!saving && (isEdit ? "Update" : "Add")}
          </button>
        </div>
      </div>
    </dialog>
  )
}
